import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { calendarLabel } from '../utils/appDate';
import { T, FONT_STACK } from '../containers/customer/customers.theme';
import {
  CloseIcon,
  CustomersKeyframes,
  PlusIcon,
  SearchIcon,
} from '../containers/customer/CustomersUI';
import { EmptyState, LoadingLine, SearchField, TagIcon } from '../containers/menu/MenuUI';
import DateRangePopover from '../containers/orders/DateRangePopover';

import ConfirmDialog from '../containers/admin/ConfirmDialog';
import ExpenseDialog from '../containers/expenses/ExpenseDialog';
import ExpenseCategoriesDialog from '../containers/expenses/ExpenseCategoriesDialog';
import ExpenseList from '../containers/expenses/ExpenseList';
import { ExpenseCategories, ExpenseHeadline } from '../containers/expenses/ExpenseSummary';

import { COPY } from '../containers/expenses/expenses.copy';
import {
  categoryNameOf,
  sortByDateDesc,
  summarise,
  vendorNameOf,
} from '../containers/expenses/expenses.summary';
import {
  createExpense,
  createExpenseType,
  deleteExpense as deleteExpenseRequest,
  deleteExpenseType as deleteExpenseTypeRequest,
  fetchExpenses,
  fetchExpenseTypes,
  fetchVendors,
  updateExpense as updateExpenseRequest,
  updateExpenseType as updateExpenseTypeRequest,
} from '../containers/expenses/expensesApi';
import {
  addExpense,
  addExpenseType,
  rangeChanged,
  removeExpense,
  removeExpenseType,
  setExpenses,
  setExpensesDataFetched,
  setExpenseTypes,
  setExpenseTypesDataFetched,
  setVendors,
  setVendorsDataFetched,
  updateExpense,
  updateExpenseType,
} from '../containers/expenses/expenseSlice';

/**
 * Expenses — the phone's screen, given a desk.
 *
 * Everything the phone does is here and behaves the same way: a date range
 * that defaults to month to date, a total for that window, a per-category
 * breakdown, the transactions themselves, and a form that files a new one as
 * cash or against a vendor on invoice.
 *
 * What is different is the shape. The phone scrolls through summary, then
 * categories, then transactions. A desk has the width to put the first two in
 * a column beside the third, so a manager can change the dates and read a
 * transaction without either one moving — and a search box is added, because
 * a month of expenses on a big screen is a list you scan rather than scroll.
 */

/** Who the API lets through: `/expenses` is manager, cashier or accountant. */
const ALLOWED_ROLES = ['manager', 'cashier', 'accountant', 'superadmin-superadmin'];

export default function Expenses() {
  const dispatch = useDispatch();

  const activeUser = useSelector((state) => state.login.activeUser);
  const { business, user } = useSelector((state) => state.login.activeUser);
  const token = useSelector((state) => state.login.token);

  const {
    range,
    expenses,
    isExpensesDataFetched,
    expenseTypes,
    isExpenseTypesDataFetched,
    vendors,
    isVendorsDataFetched,
  } = useSelector((state) => state.expenses);

  const [query, setQuery] = React.useState('');
  /** The category name picked out of the breakdown, or null for all of them. */
  const [activeCategory, setActiveCategory] = React.useState(null);
  const [notice, setNotice] = React.useState(null);

  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState('');

  const [creating, setCreating] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [deleting, setDeleting] = React.useState(null);
  const [managingCategories, setManagingCategories] = React.useState(false);

  const [datesOpen, setDatesOpen] = React.useState(false);
  const [vendorsLoading, setVendorsLoading] = React.useState(false);
  const dateAnchor = React.useRef(null);

  const allowed = ALLOWED_ROLES.indexOf(activeUser?.role) !== -1;

  const say = React.useCallback((message, isError) => {
    setNotice({ message, isError, at: Date.now() });
  }, []);

  React.useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), notice.isError ? 6000 : 3000);
    return () => clearTimeout(timer);
  }, [notice]);

  /* ── the rows ───────────────────────────────────────────────────────────
   *
   * Refetched whenever the window changes, and on first entry. Not cached
   * across visits, for the same reason the phone refetches: an expense
   * entered on another till has to be here.
   */

  React.useEffect(() => {
    if (!allowed || !business?._id || isExpensesDataFetched) return undefined;

    let cancelled = false;
    setLoading(true);
    setLoadError('');

    fetchExpenses({ businessId: business._id, start: range.start, end: range.end, token })
      .then((rows) => {
        if (cancelled) return;
        dispatch(setExpenses(sortByDateDesc(rows)));
        dispatch(setExpensesDataFetched(true));
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err?.response?.data?.message || COPY.errorTitle);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [allowed, business, token, range, isExpensesDataFetched, dispatch]);

  /* ── the categories ── */

  React.useEffect(() => {
    if (!allowed || !business?._id || isExpenseTypesDataFetched) return undefined;

    let cancelled = false;

    fetchExpenseTypes({ businessId: business._id, token })
      .then((rows) => {
        if (cancelled) return;
        dispatch(setExpenseTypes(rows));
        dispatch(setExpenseTypesDataFetched(true));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [allowed, business, token, isExpenseTypesDataFetched, dispatch]);

  /** Fetched the first time somebody actually chooses Invoice. */
  const loadVendors = React.useCallback(() => {
    if (!business?._id || isVendorsDataFetched || vendorsLoading) return;

    setVendorsLoading(true);
    fetchVendors({ businessId: business._id, token })
      .then((rows) => {
        dispatch(setVendors(rows));
        dispatch(setVendorsDataFetched(true));
      })
      .catch(() => {})
      .finally(() => setVendorsLoading(false));
  }, [business, token, isVendorsDataFetched, vendorsLoading, dispatch]);

  /* ── writing ────────────────────────────────────────────────────────── */

  /**
   * The category object for an id, so a saved row can carry its own name.
   *
   * The API populates `expenseType` when it lists expenses but not when it
   * echoes one back from a create or an update, so what returns has an id
   * where the list has an object. Re-attaching it here is what keeps the row
   * showing a category name instead of "Unknown" until the next refresh.
   */
  const categoryFor = React.useCallback(
    (categoryId) => expenseTypes.find((row) => String(row._id) === String(categoryId)) || null,
    [expenseTypes]
  );

  /** Likewise for the vendor, which the same two responses leave as an id. */
  const vendorFor = React.useCallback(
    (vendorId) => vendors.find((row) => String(row._id) === String(vendorId)) || null,
    [vendors]
  );

  const withNames = React.useCallback(
    (saved, categoryId) => ({
      ...saved,
      expenseType: categoryFor(categoryId) || saved?.expenseType || null,
      vendor:
        saved?.paymentType === COPY.invoice
          ? vendorFor(saved?.vendor?._id || saved?.vendor) || saved?.vendor || null
          : null,
    }),
    [categoryFor, vendorFor]
  );

  const submitExpense = async ({ expense, payload, categoryId }) => {
    if (expense) {
      const saved = await updateExpenseRequest({ id: expense._id, payload, token });
      dispatch(
        updateExpense({
          _id: expense._id,
          updated: withNames(saved || payload, categoryId),
        })
      );
      say(COPY.saved);
      return;
    }

    const created = await createExpense({
      payload: { ...payload, business: business?._id, user: user?._id },
      token,
    });
    dispatch(addExpense(withNames(created, categoryId)));
    say(COPY.added);
  };

  const removeExpenseRow = async () => {
    await deleteExpenseRequest({ id: deleting._id, token });
    dispatch(removeExpense(deleting._id));
    say(COPY.deleted);
  };

  const createCategory = async ({ name }) => {
    const created = await createExpenseType({
      payload: { name, business: business?._id },
      token,
    });
    dispatch(addExpenseType(created));
    say(COPY.categoryAdded);
  };

  const renameCategory = async ({ id, name }) => {
    const saved = await updateExpenseTypeRequest({ id, payload: { name }, token });
    dispatch(updateExpenseType({ _id: id, updated: saved || { name } }));
    say(COPY.categoryUpdated);
  };

  const deleteCategory = async (category) => {
    await deleteExpenseTypeRequest({ id: category._id, token });
    dispatch(removeExpenseType(category._id));
    say(COPY.categoryDeleted);
  };

  /* ── what is on screen ──────────────────────────────────────────────── */

  /** Everything the search matches — before a category is picked from it. */
  const searched = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return expenses;

    return expenses.filter(
      (expense) =>
        String(expense.description || '').toLowerCase().indexOf(q) !== -1 ||
        String(expense.notes || '').toLowerCase().indexOf(q) !== -1 ||
        categoryNameOf(expense).toLowerCase().indexOf(q) !== -1 ||
        vendorNameOf(expense).toLowerCase().indexOf(q) !== -1
    );
  }, [expenses, query]);

  /**
   * The breakdown is built BEFORE the category filter, so every line stays on
   * screen and stays clickable. Narrowing it to the line just picked would
   * leave nothing to pick next, and no way to see what the rest of the month
   * looked like.
   */
  const breakdown = React.useMemo(() => summarise(searched).categories, [searched]);

  /** The rows in the list: the search, then the category if one is chosen. */
  const visible = React.useMemo(() => {
    if (!activeCategory) return searched;
    return searched.filter((expense) => categoryNameOf(expense) === activeCategory);
  }, [searched, activeCategory]);

  /**
   * The headline follows the list.
   *
   * A total that ignored the filters would disagree with the rows underneath
   * it, and the first thing anyone does with a filtered list is add it up. So
   * picking Rent turns this into what rent cost — which is the question that
   * made them click it.
   */
  const summary = React.useMemo(() => summarise(visible), [visible]);

  /**
   * A category can stop existing under you — the search narrowed past it, the
   * last expense in it was deleted, the period changed. Holding a filter that
   * matches nothing would show an empty list with no clue why.
   */
  React.useEffect(() => {
    if (!activeCategory) return;
    if (breakdown.some((line) => line.name === activeCategory)) return;
    setActiveCategory(null);
  }, [breakdown, activeCategory]);

  // Plain calendar strings that never went near a server, so they are read as
  // written rather than through the wall-clock rule.
  const rangeLabel = React.useMemo(
    () => `${calendarLabel(range.start)} — ${calendarLabel(range.end)}`,
    [range]
  );

  /* ── not for waiters ────────────────────────────────────────────────── */

  if (!allowed) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 90px)',
          background: T.canvas,
          fontFamily: FONT_STACK,
        }}
      >
        <span style={{ fontSize: 14, color: T.inkSoft }}>{COPY.restricted}</span>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 90px)',
        background: T.canvas,
        color: T.ink,
        fontFamily: FONT_STACK,
        overflow: 'hidden',
      }}
    >
      <CustomersKeyframes />

      {/* ── the bar ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 20,
          padding: '16px 28px',
          background: T.surface,
          borderBottom: `1px solid ${T.border}`,
        }}
      >
        <span style={{ fontSize: 16, fontWeight: 700 }}>{COPY.title}</span>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 260 }}>
            <SearchField
              value={query}
              onChange={setQuery}
              placeholder={COPY.search}
              icon={<SearchIcon />}
            />
          </div>

          <button
            type="button"
            onClick={() => setManagingCategories(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              height: 42,
              padding: '0 16px',
              background: T.surface,
              border: `1px solid ${T.borderStrong}`,
              borderRadius: T.radiusPill,
              font: 'inherit',
              fontSize: 13.5,
              fontWeight: 600,
              color: T.inkSoft,
              cursor: 'pointer',
            }}
          >
            <TagIcon size={16} />
            <span>{COPY.manageCategories}</span>
          </button>

          <button
            type="button"
            onClick={() => setCreating(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              height: 42,
              padding: '0 18px',
              background: T.brand,
              border: 'none',
              borderRadius: T.radiusPill,
              font: 'inherit',
              fontSize: 13.5,
              fontWeight: 600,
              color: T.surface,
              cursor: 'pointer',
            }}
          >
            <PlusIcon />
            <span>{COPY.add}</span>
          </button>
        </div>
      </div>

      {/* ── the list, with the summary beside it ── */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          // The transactions take the room; the summary is the rail. The
          // rail is the SECOND child on purpose — it paints over the list,
          // which is what lets the date popover hang off it.
          gridTemplateColumns: 'minmax(0, 1fr) minmax(300px, 340px)',
          gap: 20,
          padding: '20px 28px 28px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            overflow: 'hidden',
          }}
        >
          {/* What the list is showing, and the second way to clear it — the
              chip is beside the rows it filtered, where the question comes up. */}
          {activeCategory ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  height: 30,
                  padding: '0 6px 0 12px',
                  background: T.selectedBg,
                  border: `1px solid ${T.selectedBorder}`,
                  borderRadius: T.radiusPill,
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: T.brandInk,
                }}
              >
                {COPY.inCategory(activeCategory)}
                <button
                  type="button"
                  aria-label={COPY.showAll}
                  onClick={() => setActiveCategory(null)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 20,
                    height: 20,
                    background: 'transparent',
                    border: 'none',
                    borderRadius: T.radiusPill,
                    color: T.brandInk,
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  <CloseIcon size={13} />
                </button>
              </span>
            </div>
          ) : null}

          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 4 }}>
            {loadError ? (
              <div style={{ padding: 60, textAlign: 'center' }}>
                <div style={{ fontSize: 14, color: T.danger }}>{loadError}</div>
                <button
                  type="button"
                  onClick={() => dispatch(setExpensesDataFetched(false))}
                  style={{
                    marginTop: 14,
                    height: 38,
                    padding: '0 18px',
                    background: T.surface,
                    border: `1px solid ${T.borderStrong}`,
                    borderRadius: T.radiusPill,
                    font: 'inherit',
                    fontSize: 13,
                    fontWeight: 600,
                    color: T.inkSoft,
                    cursor: 'pointer',
                  }}
                >
                  {COPY.retry}
                </button>
              </div>
            ) : loading && !expenses.length ? (
              <LoadingLine label={COPY.loading} />
            ) : visible.length ? (
              <ExpenseList
                expenses={visible}
                busyId={deleting?._id}
                onEdit={setEditing}
                onDelete={setDeleting}
              />
            ) : (
              <EmptyState
                icon={<TagIcon />}
                title={
                  activeCategory
                    ? COPY.noneInCategory(activeCategory)
                    : query.trim()
                    ? COPY.nothingFound
                    : COPY.emptyTitle
                }
              />
            )}
          </div>
        </div>

        {/* Not scrollable on purpose: the date popover hangs below the
            headline card and a scrolling column would cut it off. The
            breakdown inside scrolls instead. */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            minHeight: 0,
          }}
        >
          <ExpenseHeadline
            total={summary.total}
            count={summary.count}
            rangeLabel={rangeLabel}
            anchorRef={dateAnchor}
            onPickDate={() => setDatesOpen((open) => !open)}
          >
            {datesOpen ? (
              <DateRangePopover
                range={range}
                anchorRef={dateAnchor}
                onClose={() => setDatesOpen(false)}
                onApply={(next) => {
                  dispatch(rangeChanged(next));
                  setDatesOpen(false);
                }}
              />
            ) : null}
          </ExpenseHeadline>

          <ExpenseCategories
            categories={breakdown}
            activeName={activeCategory}
            onSelect={setActiveCategory}
          />
        </div>
      </div>

      {/* ── what just happened ── */}
      {notice ? (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            bottom: 24,
            transform: 'translateX(-50%)',
            maxWidth: 460,
            padding: '11px 18px',
            background: notice.isError ? T.danger : T.ink,
            borderRadius: T.radiusPill,
            fontSize: 13,
            fontWeight: 600,
            color: T.surface,
            boxShadow: T.dialog,
          }}
        >
          {notice.message}
        </div>
      ) : null}

      {/* ── dialogs ── */}
      {creating || editing ? (
        <ExpenseDialog
          expense={editing}
          categories={expenseTypes}
          vendors={vendors}
          vendorsLoading={vendorsLoading}
          onNeedVendors={loadVendors}
          onManageCategories={() => setManagingCategories(true)}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSubmit={submitExpense}
        />
      ) : null}

      {managingCategories ? (
        <ExpenseCategoriesDialog
          categories={expenseTypes}
          onClose={() => setManagingCategories(false)}
          onCreate={createCategory}
          onRename={renameCategory}
          onDelete={deleteCategory}
        />
      ) : null}

      {deleting ? (
        <ConfirmDialog
          title={COPY.deleteTitle}
          body={COPY.deleteBody(deleting.description)}
          note={deleting.paymentType === COPY.invoice ? COPY.deleteNote : undefined}
          confirmLabel={COPY.delete}
          cancelLabel={COPY.cancel}
          onClose={() => setDeleting(null)}
          onConfirm={removeExpenseRow}
        />
      ) : null}
    </div>
  );
}
